import { FC, HTMLAttributes, MutableRefObject, ReactNode } from 'react';
import styled from 'styled-components';
import { getCurrentEnvironment } from '@belong/env';
import { logger } from '@belong/logging';
import { FIELDSET_VERTICAL_SPACING, FONT_WEIGHT, getBodyFont, isFocused } from '@belong/themes';
import { ENVS, ITestable } from '@belong/types';

/*
  Fieldset sets a spacing of 0.8rem between its children.

  However, to have an accessible inline validation,
  the Error component is expected to be mounted
  while its content is dynamically generated

  So we do not apply the margin-top on the div[aria-live]
  but on its children instead.
*/

export interface IFieldset extends ITestable, HTMLAttributes<HTMLFieldSetElement> {
  legend: string;
  children?: ReactNode;
  isFocusable?: boolean;
  forwardRef?: MutableRefObject<HTMLFieldSetElement | null>;
}

export const FieldsetElement = styled.fieldset`
  border: none;

  &:focus-visible {
    ${isFocused()};
  }

  > * + *:not([aria-live]) {
    margin-top: ${FIELDSET_VERTICAL_SPACING};
  }

  > [aria-live] > * {
    margin-top: ${FIELDSET_VERTICAL_SPACING};
  }
`;

/**
 * This component aims to handle vertical spacing for form item.
 *
 * See also `InputsWrapper`
 */
const Legend = styled.legend`
  // Hide element from UI, if hideLegend is true, but remain visible to screen-reader: https://webaim.org/techniques/css/invisiblecontent/
  // Alternatively show if hideLegend is false
  position: relative;
  display: block;
  font-weight: ${FONT_WEIGHT.SEMI_BOLD};
  color: ${({ theme }) => theme.foregroundColor};
  ${getBodyFont('large')}
`;

export const Fieldset: FC<IFieldset> = ({ legend, children, isFocusable, forwardRef, ...props }) => {
  // Enforcing legend, so an empty string cannot be passed in
  if (!legend.trim()) {
    const errorMessage = 'Fieldset: "legend" is required, it must not be an empty string';
    if (getCurrentEnvironment() !== ENVS.PRODUCTION) {
      throw new Error(errorMessage);
    } else {
      logger.warn(errorMessage);
    }
  }

  return (
    <FieldsetElement {...props} ref={forwardRef} tabIndex={isFocusable ? 0 : undefined}>
      <Legend>{legend}</Legend>
      {children}
    </FieldsetElement>
  );
};

Fieldset.displayName = 'Fieldset';
